<template>
  <div id="profile" class="page-top">
    <section v-if="currentUserProfile">
      <div class="col1">
        <div class="header">
          <h1 v-html="headerTitle"/>
          <h6 class="package-name" v-html="packageName" @click="setUserPackage"/>
          <ul class="action-links">
            <li v-if="isAdmin && currentUserProfile && userProfile && currentUserProfile.uid !== userProfile.uid">
              <a class="view-timeline-link" @click="$router.push({ path: '/dashboard', query: { uid: currentUserProfile.uid }})">{{currentUserProfile.firstName}}'s timeline</a>
            </li>
            <li v-if="((!isAdmin && !emailVerified) || emailVerified) && showVerificationStatus">
              <a v-if="!isAdmin && !emailVerified" class="verify-email" href="#" @click.prevent="verifyEmail" v-show="!verifyClicked">Verify your email</a>
              <span v-if="emailVerified">
                Email Verified!
              </span>
            </li>
            <li v-if="isAdmin && !adminIsEditingSelf && currentUserProfile.timelineStep == 7">
              <a class="send-action-required" href="#" @click.prevent="sendActionRequiredEmail" title="Click after attorney submits feedback on user uploaded supporting documents">Notify client of attorney feedback</a>
            </li>
          </ul>
        </div>
        <div class="tabbed-content">
          <div class="tabbed-content__tabs" v-if="currentUserProfile && !currentUserProfile.isAdmin">
            <a href="#"
              :class="{ active: activeTab === 'profile'}"
              @click.prevent="setActiveTab('profile')"
              >
              Profile
            </a>
            <a href="#"
              v-if="shouldShowSupportingDetailsTab"
              :class="{ active: activeTab === 'supporting-details'}"
              @click.prevent="setActiveTab('supporting-details')">
              Supporting Details
            </a>
            <a href="#"
              v-if="shouldShowPatentApplicationTab"
              :class="{ active: activeTab === 'application'}"
              @click.prevent="setActiveTab('application')"
              >
              Patent Application
            </a>
            <a href="#"
              v-if="shouldShowPaymentTab"
              :class="{ active: activeTab === 'payment'}"
              @click.prevent="setActiveTab('payment')"
              >
              Payment Details
            </a>
          </div>
          <div class="tabbed-content__content" v-show="activeTab === 'profile'">
            <FormulateForm
              ref="editProfileForm"
              v-model="profileFormValues"
              v-if="profileFormValues && showForm"
              :schema="profileSchema"
              @submit="profileSubmit"
              @failed-validation="failedValidation"
            />
            <small style="position:relative;top:-10px">Note: updating your email address will log you out</small>
            <br/>
            <br/>
            <p>
              <a href="#" @click.prevent="resetPassword">Reset password</a>
            </p>
          </div>
          <div class="tabbed-content__content" v-show="activeTab === 'supporting-details'">
            <FormulateForm
              ref="supportingDetailsForm"
              v-model="supportingDetailsFormValues"
              v-if="supportingDetailsFormValues && showForm"
              :schema="supportingDetailsSchema"
              @submit="supportingDetailsSubmit"
              @failed-validation="failedValidation"
            />
          </div>
          <div class="tabbed-content__content" v-show="activeTab === 'application'">
            <FormulateForm
              ref="applicationForm"
              v-model="applicationFormValues"
              v-if="applicationFormValues && showForm"
              :schema="applicationSchema"
              @submit="applicationSubmit"
              @failed-validation="failedValidation"
            />
          </div>
          <div class="tabbed-content__content" v-show="activeTab === 'payment'">
            <!-- Payment form for custom packages only -->
            <FormulateForm
              ref="paymentForm"
              v-model="paymentFormValues"
              v-if="isAdmin && paymentFormValues && showForm && currentUserProfile.package === 'custom'"
              :schema="paymentSchema"
              @submit="paymentSubmit"
              @failed-validation="failedValidation"
            />
            <PaymentPanel v-if="currentUserProfile.stripe"/>
          </div>
        </div>

        <div class="footer">
          <!-- Save button -->
          <button @click="saveButtonClicked" :disabled="pending" class="button" v-html="pending ? 'Please Wait...' : 'Save'"/>
          <a href="#" class="delete" @click.prevent="deleteAccount">Delete Account</a>
        </div>
        <p class="progress" v-if="uploadProgress !== 0 && uploadProgress !== 100" v-html="`Uploading documents: ${uploadProgress}% complete`"/>
      </div>
    </section>
  </div>
</template>

<script>
import { deleteUser } from '@/library/userFunctions'
import { loadStripe } from '@stripe/stripe-js'
import { mapGetters, mapState } from 'vuex'
import { isClientEmailVerified } from '@/library/userFunctions'
import * as fb from '@/library/firebase'
import router from '@/router'
import applicationSchema from '@/forms/applicationSchema'
import editProfileSchema from '@/forms/editProfileSchema'
import PaymentPanel from '@/components/PaymentPanel'
import paymentSchema from '@/forms/paymentSchema'
import signupFormSchema from '@/forms/signupFormSchema'
import signupFormSchemaSimple from '@/forms/signupFormSchemaSimple'
import updateProfileMixin from '@/mixins/updateProfileMixin'
const sendEmail = fb.funcs.httpsCallable('sendEmail')

export default {
  name: 'UpdateProfile',
  data() {
    return {
      activeTab: 'profile',
      emailFetched: false,
      emailVerified: false,
      applicationFormValues: {
        asFiled: null,
        draft: null,
        draftFeedback: null,
        filingDate: '',
        filingReceipt: null,
        serialNumber: ''
      },
      paymentFormValues: {
        customPrice: null
      },
      newPackage: null,
      showVerificationStatus: false,
      userId: '',
      verifyClicked: false
    }
  },
  async mounted() {
    const tabName = window.location.hash ? window.location.hash.replace('#','') : '';
    if (tabName) {
      this.activeTab = tabName
    }
    else {
      this.activeTab = 'profile'
    }

    this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)

    this.updateEmailVerified()
  },
  components: {
    PaymentPanel
  },
  computed: {
    ...mapState([
      'isAdmin',
      'userProfile'
    ]),
    ...mapGetters([
      'currentUserProfile'
    ]),
    profileSchema() {
      let schema = [];
      // editProfileSchema contains admin-editable only fields
      if (this.isAdmin) {
        schema.push(...editProfileSchema);
      }

      // Reuse the form schema from the signup form for the rest of the fields
      if (this.currentUserProfile.supportingDetails) {
        schema.push(...signupFormSchema)
      }
      else {
        schema.push(...signupFormSchemaSimple)
      }

      // // Make it so filling out passwords is not required
      // const i = schema.findIndex(o => o.name === 'passwords')
      // schema[i].children.forEach(c => {
      //   c.label = c.label.replace('*', '').replace('Create a', 'Update')
      //   c.validation = c.validation.replace('^required|','')
      // })

      // Hide the certification section of the signup form
      return schema.filter(o => {
        const hideFor = ['passwords'];
        return !hideFor.some(term => o.name?.includes(term))
      })
    },
    headerTitle() {
      const word = this.isAdmin && !this.adminIsEditingSelf ? `${this.currentUserProfile.firstName}'s` : 'My'
      return `Update ${word} Profile`
    },
    packageName() {
      const pkg = this.newPackage || this.currentUserProfile.package
      if (!pkg) {
        return ''
      }
      const uppercasedPackageName = pkg?.charAt(0).toUpperCase() + pkg?.slice(1)
      return `Package: ${uppercasedPackageName}`;
    },
    adminIsEditingSelf() {
      return this.isAdmin && this.currentUserProfile?.uid === this.userProfile?.uid
    },
    applicationSchema() {
      return applicationSchema
    },
    paymentSchema() {
      return paymentSchema
    },
    shouldShowPatentApplicationTab() {
      if (this.isAdmin) {
        return !this.adminIsEditingSelf;
      }
      return false;
    },
    shouldShowPaymentTab() {
      if (this.isAdmin) {
        return !this.adminIsEditingSelf;
      }
      return this.currentUserProfile.stripe;
    },
    shouldShowSupportingDetailsTab() {
      if (this.isAdmin) {
        return !this.adminIsEditingSelf;
      }
      return this.currentUserProfile.supportingDetails && this.currentUserProfile.timelineStep >= 6;
    }
  },
  methods: {
    async setUserPackage() {
      if (!this.isAdmin) {
        return
      }
      let response = prompt('Which package to change to? (Possible values: custom, enhanced, basic, expedited)')
      if (!response) {
        return
      }
      const uid = this.currentUserProfile.uid
      if (['custom', 'basic', 'enhanced', 'expedited'].some(pkg => pkg === response)) {
        this.newPackage = response
        await fb.usersCollection.doc(uid).set({ package: response }, { merge: true })
      }
      else {
        alert('Invalid package name')
      }
    },
    updateEmailVerified() {
      if (this.currentUserProfile) {
        this.showVerificationStatus = this.currentUserProfile.isAdmin ? false : true
        this.emailVerified = isClientEmailVerified(this.currentUserProfile.uid)
      }
    },
    async resetPassword() {
      const user = await fb.auth.currentUser;
      const password = prompt('Enter new password')
      if (!password) {
        return
      }
      const password2 = prompt('Repeat the password')
      if (!password || password !== password2) {
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: 'Passwords do not match. Try again.',
          type: 'error'
        })
        return
      }
      if (password) {
        await user.updatePassword(password).catch(error => {
          this.$notify({
            duration: 10000,
            group: 'default',
            title: 'Error',
            text: error,
            type: 'error'
          })
        })
      }
    },
    failedValidation() {
      this.pending = false
    },
    setActiveTab(tab) {
      this.activeTab = tab
      window.history.pushState({}, null, `#${tab}`)
    },
    deleteAccount() {
      if (confirm('Are you sure you want to delete your account?') == true) {
        deleteUser(this.currentUserProfile.uid).then(response => {
          if (response) {
            router.push('/').catch(() => {})
          }
        }).catch(err => {
          this.$notify({
            group: 'default',
            title: 'Error',
            text: err,
            type: 'error',
            duration: 10000
          })
        })
      }
    },
    async profileSubmit() {
      this.uploadAndSave(this.profileFormValues)
    },
    applicationSubmit() {
      this.uploadAndSave(this.applicationFormValues, 'application', null, ['draft', 'asFiled', 'filingReceipt'])
    },
    paymentSubmit() {
      this.uploadAndSave(this.paymentFormValues, 'stripe')
    },
    saveButtonClicked() {
      this.pending = true
      switch (this.activeTab) {
        case 'profile':
          this.$refs.editProfileForm.formSubmitted()
          break
        case 'supporting-details':
          this.$refs.supportingDetailsForm.formSubmitted()
          break
        case 'application':
          this.$refs.applicationForm.formSubmitted()
          break
        case 'payment':
          this.$refs.paymentForm.formSubmitted()
          break
      }
    },
    verifyEmail() {
      this.verifyClicked = true;
      fb.auth.currentUser.sendEmailVerification().then(() => {
        this.$notify({
          group: 'default',
          title: 'Success',
          text: `Sent email verification to ${this.currentUserProfile.email}`
        })
      }).catch((error) => {
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: error.message,
          type: 'error'
        })
      })
    },
    sendActionRequiredEmail() {
      const customMessage = prompt('Which fields or documents are incomplete? This will be inserted into the email.')
      if (customMessage) {
        const to = this.currentUserProfile.email
        const subject = 'Action required: Documents are incomplete or need additional information.'
        let message = `<p>${this.currentUserProfile.firstName},</p>`
        message += '<p>You need to resubmit or respond to the comments/questions below from the attorney regarding the following uploaded supporting documents:</p>'
        message += `<p>${customMessage}</p>`
        message += '<p>Best regards,</p>'
        message += `<p>The <a href="${process.env.VUE_APP_SITE_URL}">PortlandPatent.com</a> team</p>`
        sendEmail({ to, subject, message }).then(() => {
          this.$notify({
            group: 'default',
            title: 'Success',
            text: `Successfully sent message to ${to}`
          })
        }).catch((error) => {
          this.pending = false
          this.$notify({
            duration: 10000,
            group: 'default',
            title: 'Error',
            type: 'error',
            text: error
          })
        })
      }
    }
  },
  mixins: [
    updateProfileMixin
  ],
  watch: {
    currentUserProfile: 'updateEmailVerified'
  }
}
</script>
<style lang="scss" scoped>
#profile {
  @include formulateStyles;

  h1 {
    font-size: 1.6rem;
  }

  .col1 {
    width: 100%;
    max-width: 60rem;
    background: $white;
    padding: 1rem 2rem;
    position: relative;
    @media ($sm-down) {
      padding: 1rem;
      margin: 0;
    }
    .action-links {
      display: flex;
      li {
        list-style: none;
        margin-right: 20px;
        padding-right: 20px;
        border-right: solid 1px #ccc;
        &:last-child {
          border-right: none;
        }
      }
    }
  }

  .success {
    color: $success;
    margin: 0.5rem 0 -2rem;
  }
}

.tabbed-content {
  &__tabs {
    margin-top: 2rem;
    display: flex;
    @media ($xs-down) {
      overflow: auto;
    }
    a {
      border: solid 1px #ccc;
      padding: 1rem;
      margin-right: 5px;
      position: relative;
      top: 1px;
      background-color: #eee;
      &.active {
        background-color: white;
        border-bottom-color: white;
      }
    }
  }
  &__content {
    padding: 2em;
    border: solid 1px #ccc;
    @media ($sm-down) {
     padding: 1em;
    }
  }
}

.footer {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;

  .delete {
    color: $error;
    display: block;
  }
}
.progress {
  margin: 1rem 0;
}
.package-name {
  font-size: 12px;
  position: absolute;
  top: 0;
  right: 30px;
  color: gray;
  @media ($sm-down) {
    display: none;
  }
}

/deep/ .formulate-form {
  textarea {
    height: 200px;
    &[name=draftFeedback] {
      height: 300px !important;
    }
  }
}
</style>